/**
 * WorkerProfileForm Component
 * Edit worker profile with structured fields for oilfield workers
 */

import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Slider,
  Alert,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { 
  jobsService, 
  WorkerProfile,
  OILFIELD_CERTIFICATIONS,
  OILFIELD_EQUIPMENT,
  BASINS,
  COUNTIES,
  JOB_TYPES
} from '@services/jobsService';
import { telegram } from '@services/telegram';
import { useAutoDismiss } from '../../hooks/useAutoDismiss';

interface WorkerProfileFormProps {
  profile: WorkerProfile | null;
  onSaved: () => void;
}

export function WorkerProfileForm({ profile, onSaved }: WorkerProfileFormProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useAutoDismiss(error, () => setError(null));

  // Form state
  const [formData, setFormData] = useState<Partial<WorkerProfile>>({
    full_name: '',
    phone: '',
    email: '',
    job_title: '',
    years_experience: 0,
    certifications: [],
    equipment: [],
    skills: [],
    preferred_counties: [],
    preferred_basins: ['Permian Basin', 'Delaware Basin'],
    availability: 'available',
    work_types: ['Full-time'],
    willing_to_travel: true,
    travel_radius: 50,
    profile_visibility: 'public',
    about: ''
  });

  // Initialize form with profile data
  useEffect(() => {
    if (profile) {
      setFormData({
        full_name: profile.full_name || '',
        phone: profile.phone || '',
        email: profile.email || '',
        job_title: profile.job_title || '',
        years_experience: profile.years_experience || 0,
        certifications: profile.certifications || [],
        equipment: profile.equipment || [],
        skills: profile.skills || [],
        preferred_counties: profile.preferred_counties || [],
        preferred_basins: profile.preferred_basins || ['Permian Basin', 'Delaware Basin'],
        availability: profile.availability || 'available',
        work_types: profile.work_types || ['Full-time'],
        willing_to_travel: profile.willing_to_travel !== false,
        travel_radius: profile.travel_radius || 50,
        profile_visibility: profile.profile_visibility || 'public',
        about: profile.about || '',
        resume_url: profile.resume_url
      });
    } else {
      // Pre-fill from Telegram user
      const telegramUser = telegram.getUser();
      if (telegramUser) {
        setFormData(prev => ({
          ...prev,
          full_name: `${telegramUser.first_name || ''} ${telegramUser.last_name || ''}`.trim()
        }));
      }
    }
  }, [profile]);

  const handleChange = (field: keyof WorkerProfile, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      let result;
      if (profile) {
        result = await jobsService.updateProfile(formData);
      } else {
        result = await jobsService.createProfile(formData);
      }

      if (result.success) {
        setSuccess(profile ? 'Profile updated!' : 'Profile created!');
        onSaved();
      } else {
        setError(result.error || 'Failed to save profile');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save profile');
    }

    setSaving(false);
  };

  const handleResumeUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['application/pdf', 'application/msword', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a PDF, DOC, or DOCX file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const result = await jobsService.uploadResume(file);
      
      if (result.success) {
        setFormData(prev => ({ ...prev, resume_url: result.resumeUrl }));
        setSuccess('Resume uploaded successfully!');
        
        // If resume was parsed, offer to auto-fill fields
        if (result.parsed) {
          const parsed = result.parsed;
          if (parsed.extractedEmail && !formData.email) {
            handleChange('email', parsed.extractedEmail);
          }
          if (parsed.extractedPhone && !formData.phone) {
            handleChange('phone', parsed.extractedPhone);
          }
          if (parsed.certifications?.length) {
            // Merge with existing certifications
            const merged = [...new Set([...formData.certifications || [], ...parsed.certifications])];
            handleChange('certifications', merged);
          }
          if (parsed.skills?.length) {
            const merged = [...new Set([...formData.skills || [], ...parsed.skills])];
            handleChange('skills', merged);
          }
          if (parsed.yearsExperience && !formData.years_experience) {
            handleChange('years_experience', parsed.yearsExperience);
          }
        }

        telegram.hapticFeedback('success');
      } else {
        setError(result.error || 'Failed to upload resume');
        telegram.hapticFeedback('error');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to upload resume');
      telegram.hapticFeedback('error');
    }

    setUploading(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Resume Upload */}
      <Box sx={{ 
        p: 2, 
        backgroundColor: 'rgba(59, 130, 246, 0.1)', 
        borderRadius: 1,
        border: '1px dashed rgba(59, 130, 246, 0.3)'
      }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx"
          style={{ display: 'none' }}
          onChange={handleResumeUpload}
        />
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {formData.resume_url ? (
            <>
              <DescriptionIcon sx={{ color: '#22c55e', fontSize: 32 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: 'white', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 16 }} />
                  Resume uploaded
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  Click to replace
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <UploadFileIcon sx={{ color: '#3b82f6', fontSize: 32 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: 'white' }}>
                  Upload your resume
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  PDF, DOC, or DOCX • Max 10MB
                </Typography>
              </Box>
            </>
          )}
          
          <Button
            variant="outlined"
            size="small"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            sx={{ 
              borderColor: 'rgba(59, 130, 246, 0.5)',
              color: '#3b82f6'
            }}
          >
            {uploading ? <CircularProgress size={20} /> : 'Upload'}
          </Button>
        </Box>
      </Box>

      {/* Basic Info */}
      <Typography variant="subtitle2" sx={{ color: '#3b82f6', mt: 1 }}>
        Basic Information
      </Typography>
      
      <TextField
        fullWidth
        size="small"
        label="Full Name"
        value={formData.full_name}
        onChange={(e) => handleChange('full_name', e.target.value)}
        sx={textFieldSx}
      />

      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          size="small"
          label="Phone"
          value={formData.phone}
          onChange={(e) => handleChange('phone', e.target.value)}
          sx={textFieldSx}
        />
        <TextField
          fullWidth
          size="small"
          label="Email"
          value={formData.email}
          onChange={(e) => handleChange('email', e.target.value)}
          sx={textFieldSx}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          size="small"
          label="Job Title / Role"
          placeholder="e.g., Driller, Roustabout, Operator"
          value={formData.job_title}
          onChange={(e) => handleChange('job_title', e.target.value)}
          sx={textFieldSx}
        />
        <TextField
          size="small"
          label="Years Exp"
          type="number"
          value={formData.years_experience}
          onChange={(e) => handleChange('years_experience', parseInt(e.target.value) || 0)}
          sx={{ ...textFieldSx, width: 120 }}
        />
      </Box>

      {/* Certifications */}
      <Typography variant="subtitle2" sx={{ color: '#3b82f6', mt: 1 }}>
        Certifications
      </Typography>
      
      <Autocomplete
        multiple
        options={OILFIELD_CERTIFICATIONS}
        value={formData.certifications || []}
        onChange={(_, value) => handleChange('certifications', value)}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip 
              {...getTagProps({ index })} 
              key={option}
              label={option} 
              size="small"
              sx={{ 
                backgroundColor: 'rgba(245, 158, 11, 0.2)',
                color: 'rgba(245, 158, 11, 0.9)'
              }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField {...params} size="small" placeholder="Select certifications" sx={textFieldSx} />
        )}
        sx={{ '& .MuiAutocomplete-tag': { color: 'white' } }}
      />

      {/* Equipment */}
      <Typography variant="subtitle2" sx={{ color: '#3b82f6', mt: 1 }}>
        Equipment You Own/Operate
      </Typography>
      
      <Autocomplete
        multiple
        options={OILFIELD_EQUIPMENT}
        value={formData.equipment || []}
        onChange={(_, value) => handleChange('equipment', value)}
        freeSolo
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip 
              {...getTagProps({ index })} 
              key={option}
              label={option} 
              size="small"
              sx={{ 
                backgroundColor: 'rgba(34, 197, 94, 0.2)',
                color: 'rgba(34, 197, 94, 0.9)'
              }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField {...params} size="small" placeholder="Select or add equipment" sx={textFieldSx} />
        )}
      />

      {/* Skills */}
      <Typography variant="subtitle2" sx={{ color: '#3b82f6', mt: 1 }}>
        Skills
      </Typography>
      
      <Autocomplete
        multiple
        options={[
          'Drilling', 'Completions', 'Workover', 'Wireline', 'Coiled Tubing',
          'Pumping', 'Cementing', 'Logging', 'Perforating', 'Fracturing',
          'Production', 'Maintenance', 'Welding', 'Electrical', 'Mechanical',
          'Hydraulics', 'Pneumatics', 'Instrumentation', 'PLC', 'Automation'
        ]}
        value={formData.skills || []}
        onChange={(_, value) => handleChange('skills', value)}
        freeSolo
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip 
              {...getTagProps({ index })} 
              key={option}
              label={option} 
              size="small"
              sx={{ 
                backgroundColor: 'rgba(59, 130, 246, 0.2)',
                color: 'rgba(59, 130, 246, 0.9)'
              }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField {...params} size="small" placeholder="Select or add skills" sx={textFieldSx} />
        )}
      />

      {/* Work Preferences */}
      <Typography variant="subtitle2" sx={{ color: '#3b82f6', mt: 1 }}>
        Work Preferences
      </Typography>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <FormControl fullWidth size="small">
          <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Availability</InputLabel>
          <Select
            value={formData.availability}
            label="Availability"
            onChange={(e) => handleChange('availability', e.target.value)}
            sx={selectSx}
          >
            <MenuItem value="available">Available Now</MenuItem>
            <MenuItem value="employed">Currently Employed</MenuItem>
            <MenuItem value="not-looking">Not Looking</MenuItem>
          </Select>
        </FormControl>

        <Autocomplete
          multiple
          fullWidth
          options={JOB_TYPES}
          value={formData.work_types || []}
          onChange={(_, value) => handleChange('work_types', value)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip 
                {...getTagProps({ index })} 
                key={option}
                label={option} 
                size="small"
                sx={{ backgroundColor: 'rgba(255,255,255,0.1)', color: 'white' }}
              />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} size="small" label="Work Types" sx={textFieldSx} />
          )}
        />
      </Box>

      {/* Location Preferences */}
      <Typography variant="subtitle2" sx={{ color: '#3b82f6', mt: 1 }}>
        Location Preferences
      </Typography>

      <Autocomplete
        multiple
        options={BASINS}
        value={formData.preferred_basins || []}
        onChange={(_, value) => handleChange('preferred_basins', value)}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip 
              {...getTagProps({ index })} 
              key={option}
              label={option} 
              size="small"
              sx={{ backgroundColor: 'rgba(255,255,255,0.1)', color: 'white' }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField {...params} size="small" label="Preferred Basins" sx={textFieldSx} />
        )}
      />

      <Autocomplete
        multiple
        options={COUNTIES}
        value={formData.preferred_counties || []}
        onChange={(_, value) => handleChange('preferred_counties', value)}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip 
              {...getTagProps({ index })} 
              key={option}
              label={option} 
              size="small"
              sx={{ backgroundColor: 'rgba(255,255,255,0.1)', color: 'white' }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField {...params} size="small" label="Preferred Counties" sx={textFieldSx} />
        )}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.willing_to_travel}
              onChange={(e) => handleChange('willing_to_travel', e.target.checked)}
              sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#3b82f6' } }}
            />
          }
          label={<Typography variant="body2" sx={{ color: 'white' }}>Willing to travel</Typography>}
        />
        
        {formData.willing_to_travel && (
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              Travel radius: {formData.travel_radius} miles
            </Typography>
            <Slider
              value={formData.travel_radius}
              onChange={(_, value) => handleChange('travel_radius', value)}
              min={10}
              max={500}
              step={10}
              sx={{ color: '#3b82f6' }}
            />
          </Box>
        )}
      </Box>

      {/* About */}
      <Typography variant="subtitle2" sx={{ color: '#3b82f6', mt: 1 }}>
        About You
      </Typography>
      
      <TextField
        fullWidth
        multiline
        rows={3}
        size="small"
        placeholder="Tell employers about yourself, your experience, and what you're looking for..."
        value={formData.about}
        onChange={(e) => handleChange('about', e.target.value)}
        sx={textFieldSx}
      />

      {/* Visibility */}
      <FormControl fullWidth size="small">
        <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Profile Visibility</InputLabel>
        <Select
          value={formData.profile_visibility}
          label="Profile Visibility"
          onChange={(e) => handleChange('profile_visibility', e.target.value)}
          sx={selectSx}
        >
          <MenuItem value="public">Public - Employers can find you</MenuItem>
          <MenuItem value="private">Private - Only you can see</MenuItem>
        </Select>
      </FormControl>

      {/* Save Button */}
      <Button
        fullWidth
        variant="contained"
        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
        onClick={handleSave}
        disabled={saving}
        sx={{ 
          mt: 2,
          backgroundColor: '#22c55e',
          '&:hover': { backgroundColor: '#16a34a' }
        }}
      >
        {saving ? 'Saving...' : (profile ? 'Update Profile' : 'Create Profile')}
      </Button>
    </Box>
  );
}

// Common styles
const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255,255,255,0.05)',
    '& input, & textarea': { color: 'white' },
    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
    '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
};

const selectSx = {
  backgroundColor: 'rgba(255,255,255,0.05)',
  color: 'white',
  '& .MuiSvgIcon-root': { color: 'white' },
  '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
  '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
};

export default WorkerProfileForm;
